import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { profileService, Profile } from '../../api/profile';
import { Menu, Settings, LogOut, ChevronDown, User as UserIcon } from 'lucide-react';
import { UserRole } from '../../types/UserRole';
import { ADMIN_PATH_TITLE_MAP, CLIENT_PATH_TITLE_MAP } from './sidebarConfig';

interface NavbarBaseProps {
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  role: UserRole;
}

function getFallbackName(role: UserRole): string {
  switch (role) {
    case UserRole.ADMIN:
      return 'Administrador';
    case UserRole.CLIENT:
      return 'Cliente';
    case UserRole.USER:
      return 'Usuário';
    default:
      return 'Usuário';
  }
}

function getRoleDisplay(role: UserRole): string {
  switch (role) {
    case UserRole.ADMIN:
      return 'Administrador do Sistema';
    case UserRole.CLIENT:
      return 'Cliente';
    case UserRole.USER:
      return 'Motorista';
    default:
      return role;
  }
}

function getRoleShortDisplay(role: UserRole): string {
  switch (role) {
    case UserRole.ADMIN:
      return 'Administrador';
    case UserRole.CLIENT:
      return 'Cliente';
    case UserRole.USER:
      return 'Motorista';
    default:
      return role;
  }
}

function getPathTitleMap(role: UserRole): Record<string, string> {
  switch (role) {
    case UserRole.ADMIN:
      return ADMIN_PATH_TITLE_MAP;
    case UserRole.CLIENT:
      return CLIENT_PATH_TITLE_MAP;
    case UserRole.USER:
    default:
      return {};
  }
}

export default function NavbarBase({ toggleSidebar, isSidebarOpen: _isSidebarOpen, role }: NavbarBaseProps) {
  const { logout, user, pageTitle, setPageTitle } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const shouldUseProfileService = role === UserRole.ADMIN || role === UserRole.CLIENT;
  const shouldSetPageTitle = role === UserRole.ADMIN || role === UserRole.CLIENT;

  useEffect(() => {
    if (!shouldSetPageTitle) return;
    const pathTitleMap = getPathTitleMap(role);
    const mapped = pathTitleMap[location.pathname] || '';
    if (mapped && (!pageTitle || pageTitle.trim() === '')) {
      setPageTitle(mapped);
    }
  }, [location.pathname, setPageTitle, pageTitle, shouldSetPageTitle, role]);

  useEffect(() => {
    if (!shouldUseProfileService) return;
    if (user) {
      profileService.getMyProfile().then(setProfile).catch(() => setProfile(null));
    }
    const handler = () => {
      profileService.getMyProfile().then(setProfile).catch(() => setProfile(null));
    };
    window.addEventListener('profile-updated', handler);
    return () => window.removeEventListener('profile-updated', handler);
  }, [user, shouldUseProfileService]);

  useEffect(() => {
    if (role === UserRole.USER) return;
    if (pageTitle && pageTitle.trim()) {
      document.title = `${pageTitle} | SuaFrota`;
    } else {
      document.title = 'SuaFrota';
    }
  }, [pageTitle, role]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fallbackName = getFallbackName(role);
  const roleDisplayFull = getRoleDisplay(role);
  const roleDisplayShort = getRoleShortDisplay(role);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 lg:left-64">
      <div
        className="relative flex h-16 items-center justify-between border-b border-slate-200/80 bg-[rgba(255,255,255,0.9)] px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-[rgba(248,250,252,0.9)] sm:px-6"
        style={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
      >
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-900/5 to-transparent" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 items-center gap-3 pr-4">
          <button
            type="button"
            className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white/70 text-slate-600 shadow-[0_1px_2px_0_rgba(15,23,42,0.04)] transition-colors hover:bg-white hover:text-slate-900 lg:hidden"
            onClick={toggleSidebar}
            aria-label="Abrir menu lateral"
          >
            <Menu className="h-4 w-4 stroke-[2]" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-left text-[19px] font-bold leading-none tracking-tight text-slate-900 sm:text-[21px] md:text-[22px]">
            {pageTitle}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="relative">
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 transition-colors hover:bg-white/80 hover:shadow-[0_1px_2px_0_rgba(15,23,42,0.04)]"
              onClick={toggleMenu}
              aria-expanded={isOpen}
              aria-haspopup="true"
              aria-label="Menu do usuário"
            >
              {profile && profile.photo ? (
                <img
                  src={profile.photo}
                  alt="Perfil"
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-white shadow-[0_1px_2px_0_rgba(15,23,42,0.08)]"
                />
              ) : (
                <span className="relative inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-xs font-semibold text-white shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_4px_12px_-4px_rgba(37,99,235,0.45)] ring-2 ring-white">
                  <UserIcon className="h-3.5 w-3.5 stroke-[1.8]" />
                </span>
              )}
              <div className="hidden text-left sm:block">
                <div className="text-sm font-semibold leading-none tracking-tight text-slate-900">
                  {profile?.name || user?.name || fallbackName}
                </div>
                <div className="mt-0.5 text-[11px] font-medium text-slate-500">
                  {user?.role === role ? roleDisplayShort : user?.role}
                </div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 stroke-[2] text-slate-400" />
            </button>

            {isOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} aria-hidden="true" />
                <div className="absolute right-0 z-40 mt-2 w-64 origin-top-right rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-[0_10px_35px_-10px_rgba(15,23,42,0.25)] ring-1 ring-black/5 backdrop-blur-xl">
                  <div className="mb-1.5 px-3 pt-2 pb-3 border-b border-slate-100">
                    <p className="text-[13.5px] font-bold text-slate-800 truncate">{profile?.name || user?.name || fallbackName}</p>
                    <p className="text-[11.5px] font-medium text-slate-500 truncate mt-0.5">{user?.role === role ? roleDisplayFull : user?.role}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="group flex w-full items-center gap-3.5 rounded-xl px-3 py-2.5 transition-all hover:bg-slate-50"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-50 group-hover:text-blue-600 ring-1 ring-inset ring-slate-200 group-hover:ring-blue-200">
                      <Settings className="h-[18px] w-[18px] stroke-[1.9]" />
                    </span>
                    <span className="flex-1 text-left text-[14px] font-semibold text-slate-700 group-hover:text-slate-900">Configurações</span>
                  </Link>
                  <div className="my-1 border-t border-slate-100/80" />
                  <button
                    type="button"
                    className="group flex w-full items-center gap-3.5 rounded-xl px-3 py-2.5 transition-all hover:bg-rose-50"
                    onClick={handleLogout}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-500 transition-colors group-hover:bg-rose-100 group-hover:text-rose-700 ring-1 ring-inset ring-rose-200">
                      <LogOut className="h-[18px] w-[18px] stroke-[1.9]" />
                    </span>
                    <span className="flex-1 text-left text-[14px] font-semibold text-rose-600 group-hover:text-rose-700">Sair</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
